import React, {useCallback, useEffect, useMemo, useState} from "react";
import DamageControlTransactionsSearchSection from "../../../../../components/search-sections/transactions-trouble-shooting";
import {DefaultPaginationInfo} from "../../../../../../core/constants/enums";
import useIsMounted from "../../../../../hooks/use-is-mounted";
import {dataGridCellTextProps, DataGridColumnAlignments, DataGridColumnPinnedTypes, DataGridColumnTypes, useDataGridSavedState} from "../../../../../../packages/data-grid";
import {Tab, Tabs} from "../../../../../components/base/Tabs";
import {TransactionsTroubleShootingListDataGridColumnNames} from "../../../../../../core/constants/table/data-grid-column-keys";
import IconButton from "../../../../../components/base/button/icon_button";
import Button from "../../../../../components/base/button/button";
import PartnerDataGrid from "../../../../../containers/data-grid";
import BizPrintTemplatesDialog from "../../../../../components/dialogs/biz-print-templates";
import FixTransactionDialog from "../../../../../components/dialogs/fix-transaction-dialog";
import useSearch from "../../../../../hooks/use-search";
import {useConfigureNotebook} from "../../../../../hooks/use-notebook-config";
import DateUtils from "../../../../../../core/services/date-utils";
import {getCardMonth} from "../../../../../../core/services/utils/utils";
import useAccessLevels from "../../../../../hooks/use-access-levels";
import {ReceiptLongRounded} from "@mui/icons-material";
import TransactionReceiptDialog from "../../../../../components/dialogs/transaction-receipt-dialog";
import useBehavioralAnalyticsTool from "../../../../../hooks/use-behavioural-analytics-tool";


const initialBizPrintDialogState = {
    open: false,
    pageName: null,
    pageData: null,
}

const tabs = {
    all: 'all',
    problematic: 'problematic',
};

const defaultTab = tabs.all;

const tabsList = [
    {
        title: "All Transactions",
        value: tabs.all,
    },
    {
        title: "Problematic Only",
        value: tabs.problematic,
    }
]

const dataGridSaveConfig = {
    name: 'TransactionsTroubleShootingViewDataGrid',
    version: 1,
}


const initialFixTransactionDialogState = {
    open: false,
    transaction: null,
}

const initReceiptDialogState = {title: '', open: false, customerId: null, receiptId: null, close: () => void 0};

/**
 * The types of transaction statuses for the transaction troubleshooting feature.
 */
export const TransactionTroubleShootingStatuses = {
    okay: 1,
    missingTransaction: 2,
    outstandingBalance: 3,
    ignored: 4,
}


const TransactionsTroubleShootingView = () => {
    const operatorAccessLevels = useAccessLevels();
    const [data, setData] = useState([]);
    const [ignoring, setIgnoring] = useState([]);
    const [receiptDialogState, setReceiptDialogState] = useState(initReceiptDialogState);
    const [fixTransactionDialogState, setFixTransactionDialogState] = useState(initialFixTransactionDialogState);
    const [bizPrintDialogState, setBizPrintDialogState] = useState(initialBizPrintDialogState);
    const isMounted = useIsMounted();
    const {configureNotebookTags} = useConfigureNotebook('damage-control-transactions-view');
    const {configureBehavioralTool} = useBehavioralAnalyticsTool();

    /**
     * As soon as the component is mounted:
     * - Configures the notebook tags for the view
     * - Configures the behavioral tool for the view
     */
    useEffect(() => {
        configureNotebookTags((noteTags) => noteTags.damageControlTransactions);
        configureBehavioralTool((tags) => tags.damageControlTransactions);
    }, [configureBehavioralTool, configureNotebookTags]);

    const statusesList = React.useMemo(() => {
        return Object.values({
            okay: {
                id: TransactionTroubleShootingStatuses.okay,
                title: 'Okay',
                color: 'green'
            },
            missingTransaction: {
                id: TransactionTroubleShootingStatuses.missingTransaction,
                title: 'Missing Transaction',
                color: 'red'
            },
            outstandingBalance: {
                id: TransactionTroubleShootingStatuses.outstandingBalance,
                title: 'Outstanding Balance',
                color: 'orange'
            },
            ignored: {
                id: TransactionTroubleShootingStatuses.ignored,
                title: 'Ignored',
                color: 'grey'
            },
        })
    }, []);

    const [, dataGridSortBy, dataGridPageSize] = useDataGridSavedState({
        columns: [],
        name: dataGridSaveConfig.name,
        version: dataGridSaveConfig.version,
    });

    /**
     * Searches the transactions troubleshooting view.
     * @type {(function(UseSearchFunctionArg, boolean=): Promise<undefined|ApiResponseDs<*>>)}
     */
    const fetchEntries = useCallback(async (args, forExport = false) => {
        const forApi = {
            customerId: args.filters.customer?.customerId,
            keyword: args.filters.keyword,
            statusId: args.filters.status?.id,
            problematicOnly: args.tab === tabs.problematic,
        };
        // TODO: call api for search of transactions trouble shooting view
        const response = {
            resultFlag: true,
            data: {
                items: Array.from({length: 20}).map((_, i) => ({
                    id: i,
                    receiptId: i + 1,
                    customerTitle: `Customer ${i}`,
                    customerId: i % 2 === 0 ? 2206 : 2209,
                    statusId: (i % 4) + 1,
                    systemStatus: 'System Status',
                    authCode: '123456',
                    amount: 1000,
                    balance: 1000,
                    date: DateUtils.serverNow().format(DateUtils.withoutOffsetFormat),
                    cardType: 'Visa',
                    message: 'message',
                    cardLast4Digits: '1234',
                    cardExpiryMonth: '01',
                })),
                total: 20,
            }
        }
        if (!isMounted())
            return;
        if (response?.resultFlag && !forExport) {
            setData(response.data?.items ?? []);
            args.setPaginationInfo((prevState) => ({
                ...prevState,
                length: response.data?.total ?? 0,
            }));
        }
        if (response?.resultFlag && forExport)
            return response;
    }, [isMounted])

    /**
     * Searches among the entries based on the filters from the user.
     * @type {UseSearchFunction}
     */
    const search = useCallback(async (result) => {
        await fetchEntries(result, false);
    }, [fetchEntries]);


    const initialPaginationInfo = useMemo(() => dataGridPageSize ? {
        ...DefaultPaginationInfo,
        pageSize: dataGridPageSize
    } : DefaultPaginationInfo, [dataGridPageSize])

    const initialSearchDataSettings = useMemo(() => ({
        tab: defaultTab,
        paginationInfo: initialPaginationInfo,
        orderBy: dataGridSortBy,
    }), [initialPaginationInfo, dataGridSortBy]);

    const searchData = useSearch(search, initialSearchDataSettings)

    /**
     * Handles changes in the tabs of the type of the editions
     */
    const onTabChanged = (e, newTab) => {
        setData([]);
        searchData.setTab(newTab);
    }

    const columns = useMemo(() =>
        /** @type {DataGridColumn[]}*/
        [
            ...(operatorAccessLevels.packages.bizPrint.printTransactionInTroubleshoot ? [{
                name: TransactionsTroubleShootingListDataGridColumnNames.print,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                sortable: false,
                pinnedToggleable: false,
                resizable: false,
                visibilityToggleable: false,
                width: 0,
            }] : []),
            {
                title: "Customer",
                name: TransactionsTroubleShootingListDataGridColumnNames.customer,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Auth Code",
                name: TransactionsTroubleShootingListDataGridColumnNames.authCode,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Date",
                name: TransactionsTroubleShootingListDataGridColumnNames.date,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.date,
            },
            {
                title: "Amount",
                name: TransactionsTroubleShootingListDataGridColumnNames.amount,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.money,
            },
            {
                title: "Balance",
                name: TransactionsTroubleShootingListDataGridColumnNames.balance,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.money,
            },
            {
                title: "Status",
                name: TransactionsTroubleShootingListDataGridColumnNames.status,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                width: 140,
            },
            {
                title: "Card Type",
                name: TransactionsTroubleShootingListDataGridColumnNames.cardType,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Card Last 4 Digits",
                name: TransactionsTroubleShootingListDataGridColumnNames.cardLast4Digits,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
                width: 140,
            },
            {
                title: "Card Expiry",
                name: TransactionsTroubleShootingListDataGridColumnNames.cardExpiry,
                alignment: DataGridColumnAlignments.right,
                type: DataGridColumnTypes.string,
            },
            {
                title: "Message",
                name: TransactionsTroubleShootingListDataGridColumnNames.message,
                alignment: DataGridColumnAlignments.left,
                type: DataGridColumnTypes.string,
                sortable: false,
                width: {
                    size: 300,
                },
            },
            {
                name: TransactionsTroubleShootingListDataGridColumnNames.ignore,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
                sortable: false,
                resizable: false,
                width: 40,
            },
            {
                name: TransactionsTroubleShootingListDataGridColumnNames.fix,
                alignment: DataGridColumnAlignments.center,
                type: DataGridColumnTypes.element,
                pinned: true,
                pinnedType: DataGridColumnPinnedTypes.right,
                sortable: false,
                resizable: false,
                width: 40,
            },
        ], [operatorAccessLevels.packages.bizPrint.printTransactionInTroubleshoot])

    const [dataGridColumns, , , dataGridDensity] = useDataGridSavedState({
        columns: columns,
        version: dataGridSaveConfig.version,
        name: dataGridSaveConfig.name,
    });

    /**
     * Opens the receipt dialog
     * @type {(function(*): void)|*}
     */
    const openReceiptDialog = useCallback((data) => {
        setReceiptDialogState({
            title: 'Transaction Receipt',
            open: true,
            customerId: data.customerId,
            receiptId: data.receiptId,
            close: () => setReceiptDialogState(initReceiptDialogState)
        });
    }, [])

    /**
     * Ignores the transaction problem.
     * @type {(function(*): void)}
     */
    const ignoreTransaction = useCallback((transaction) => {
        setIgnoring(prevState => [...new Set([...prevState, transaction.id])])
        // TODO: call api for ignoring transaction
        const response = {
            resultFlag: true,
        }
        if (!isMounted())
            return
        setIgnoring(prevState => prevState.filter(e => e !== transaction.id))
        if (response?.resultFlag) {
            if (searchData.tab === tabs.problematic) {
                setData(prevState => prevState.filter(e => e.id !== transaction.id))
            } else {
                setData(prevState => prevState.map(e =>
                    e.id === transaction.id
                        ? ({...e, statusId: TransactionTroubleShootingStatuses.ignored})
                        : e
                ))
            }
        }
    }, [isMounted, searchData.tab])

    /**
     * Fixes the transaction problem.
     * @type {(function(*): void)}
     */
    const fixTransaction = useCallback((transaction) => {
        setFixTransactionDialogState({
            open: true,
            transaction: transaction,
        })
    }, [])

    /**
     * Updates the transaction properties in the state.
     * @type {(function(*, *): void)}
     */
    const updateTransaction = useCallback((transactionId, updater) => {
        setData(prevState => prevState.map(e =>
            e.id === transactionId
                ? {...e, ...updater(e)}
                : e
        ));
        if (fixTransactionDialogState.transaction && transactionId === fixTransactionDialogState.transaction.id) {
            setFixTransactionDialogState(prevState => ({
                ...prevState,
                transaction: {...prevState.transaction, ...updater(prevState.transaction)},
            }))
        }
    }, [fixTransactionDialogState.transaction])

    /**
     * Close the fix transaction dialog and removes the transaction from the problematic list if it is fixed.
     * @type {(function(): void)|*}
     */
    const closeFixTransactionDialog = useCallback(() => {
        setData(prevState => {
            const found = prevState.find(e => e.id === fixTransactionDialogState.transaction.id);
            if (found?.statusId === TransactionTroubleShootingStatuses.okay && searchData.tab === tabs.problematic) {
                return prevState.filter(e => e.id !== fixTransactionDialogState.transaction.id);
            }
            return prevState;
        })
        setFixTransactionDialogState(initialFixTransactionDialogState);
    }, [fixTransactionDialogState.transaction?.id, searchData.tab]);

    /**@type {DataGridRow[]}*/
    const dataGridRows = useMemo(() => {
        return data?.map(item => ({
            key: item.id,
            data: item,
            cells: {
                [TransactionsTroubleShootingListDataGridColumnNames.print]:
                    <IconButton
                        onPressed={() => openReceiptDialog(item)}
                        color={'paper.primary'}
                    >
                        <ReceiptLongRounded/>
                    </IconButton>,
                [TransactionsTroubleShootingListDataGridColumnNames.ignore]:
                    item.statusId !== TransactionTroubleShootingStatuses.okay && item.statusId !== TransactionTroubleShootingStatuses.ignored &&
                    <Button
                        variant={'outlined'}
                        color={'primary'}
                        disabled={ignoring.includes(item.id)}
                        onClick={() => ignoreTransaction(item)}
                    >
                        {
                            ignoring.includes(item.id)
                                ? "Ignoring..."
                                : "Ignore"
                        }
                    </Button>,
                [TransactionsTroubleShootingListDataGridColumnNames.fix]:
                    item.statusId !== TransactionTroubleShootingStatuses.okay && item.statusId !== TransactionTroubleShootingStatuses.ignored &&
                    <Button
                        variant={'outlined'}
                        color={'primary'}
                        onClick={() => fixTransaction(item)}
                    >
                        Fix
                    </Button>,
                [TransactionsTroubleShootingListDataGridColumnNames.customer]: item.customerTitle,
                [TransactionsTroubleShootingListDataGridColumnNames.authCode]: item.authCode,
                [TransactionsTroubleShootingListDataGridColumnNames.date]: item.date,
                [TransactionsTroubleShootingListDataGridColumnNames.amount]: item.amount,
                [TransactionsTroubleShootingListDataGridColumnNames.balance]: item.balance,
                [TransactionsTroubleShootingListDataGridColumnNames.status]:
                    <div {...dataGridCellTextProps} className={'d-flex align-items-center'}>
                        <div
                            className={'rounded-circle mr-2'}
                            style={{
                                width: 10,
                                height: 10,
                                backgroundColor: statusesList.find(e => e.id === item.statusId)?.color,
                            }}
                        />
                        {statusesList.find(e => e.id === item.statusId)?.title}
                    </div>,
                [TransactionsTroubleShootingListDataGridColumnNames.cardType]: item.cardType,
                [TransactionsTroubleShootingListDataGridColumnNames.cardLast4Digits]: item.cardLast4Digits,
                [TransactionsTroubleShootingListDataGridColumnNames.cardExpiry]: getCardMonth(item.cardExpiryMonth),
                [TransactionsTroubleShootingListDataGridColumnNames.message]: item.message,
            }
        }))
    }, [data, fixTransaction, ignoreTransaction, ignoring, openReceiptDialog, statusesList])

    return (
        <>
            <p className={'text-lg font-weight-semi-bold mt-2 mb-3'}>
                Payments Troubleshooting
            </p>
            <DamageControlTransactionsSearchSection searchData={searchData} statusesList={statusesList}/>
            <div className='w-100 flex-grow-1'>
                <Tabs value={searchData.tab ?? false} onChange={onTabChanged}>
                    {
                        tabsList.map(e => (
                            <Tab key={e.value} value={e.value} label={e.title}/>
                        ))
                    }
                </Tabs>
            </div>
            <div className='my-4 w-100'/>
            <PartnerDataGrid
                {...dataGridSaveConfig}
                columns={dataGridColumns}
                initialLayoutState={{
                    columns: columns,
                }}
                rows={dataGridRows}
                state={{
                    pagination: searchData.paginationInfo,
                    sortBy: searchData.orderBy,
                    loading: {state: searchData.loading},
                    density: dataGridDensity,
                }}
                hideFooterActions
                hideExporting
                onSortByChanged={(_, current) => searchData.setOrderBy(current)}
                onCurrentPageChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
                onPageSizeChanged={(_, current) => searchData.setPaginationInfo(prevState => ({...prevState, ...current}))}
            />
            <BizPrintTemplatesDialog
                open={bizPrintDialogState.open}
                close={() => setBizPrintDialogState(initialBizPrintDialogState)}
                pageName={bizPrintDialogState.pageName}
                pageData={bizPrintDialogState.pageData}
            />
            <FixTransactionDialog
                open={fixTransactionDialogState.open}
                close={closeFixTransactionDialog}
                transaction={fixTransactionDialogState.transaction}
                updateTransaction={updateTransaction}
            />
            <TransactionReceiptDialog
                title={receiptDialogState.title}
                open={receiptDialogState.open}
                customerId={receiptDialogState.customerId}
                receiptId={receiptDialogState.receiptId}
                setOpen={receiptDialogState.close}
            />
        </>
    )
}

export default TransactionsTroubleShootingView;
